// Beállítások (slice 4.5): who may use the admin, with which role — admins
// only; the navigation does not show it to anybody else, and the API answers
// them 403 anyway. The country sender settings have their own page
// (Országbeállítások); this one links there rather than repeating it.
//
// An invitation makes an `invited` user and one set-password link. The API
// mails the link to the user (system mail) and says whether it went out; the
// link is still shown once, here, with a copy button — the fallback when the
// mail is only logged or did not go out. Every change is one event in the
// user's log, shown under the row, the mail's outcome included.

import { useEffect, useId, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CopyIcon, IdCardIcon, UserPlusIcon } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  changeUserRole,
  deactivateUser,
  fetchUserEvents,
  fetchUsers,
  inviteUser,
  issueUserLink,
  reactivateUser,
} from "@/lib/api";
import type { IssuedLink, LinkMail, Role, UserEvent, UserRow } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLoad } from "@/lib/useLoad";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { ActionDialog } from "@/components/emc/ActionDialog";
import { DataTable } from "@/components/emc/DataTable";
import { Field } from "@/components/emc/Field";
import { PageHeader } from "@/components/emc/PageHeader";
import { RowActions } from "@/components/emc/RowActions";
import { StatusPill } from "@/components/emc/StatusPill";
import { Callout, ErrorState } from "@/components/emc/states";
import { minute } from "@/components/emc/format";
import { messageOf } from "@/pages/campaigns/model";
import { DigestRecipients } from "@/pages/settings/DigestRecipients";
import { Webshops } from "@/pages/settings/Webshops";
import {
  LINK_WORD,
  ROLE_LABEL,
  ROLES,
  actorLabel,
  eventLine,
  mailNote,
  passwordLinkUrl,
  userActions,
} from "@/pages/users/model";
import type { UserAction } from "@/pages/users/model";

// --- the link, shown once ----------------------------------------------------------

/** A link just issued, with whom it is for and what became of its mail. */
interface Shown {
  link: IssuedLink;
  who: string;
  email: string;
  mail: LinkMail | null;
}

function LinkDialog({ shown, onClose }: { shown: Shown | null; onClose: () => void }) {
  const inputId = useId();
  const issued = shown?.link ?? null;
  const url = issued === null ? "" : passwordLinkUrl(window.location.origin, issued.token);
  const note = shown === null ? null : mailNote(shown.mail, shown.email);

  async function copy() {
    try {
      await window.navigator.clipboard.writeText(url);
      toast.success("A link a vágólapon van");
    } catch {
      const input = document.getElementById(inputId);
      if (input instanceof HTMLInputElement) input.select();
      toast.message("Jelöld ki és másold ki kézzel");
    }
  }

  return (
    <Dialog open={issued !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{issued?.purpose === "reset" ? "Jelszó-visszaállító link" : "Meghívó link"}</DialogTitle>
          <DialogDescription>
            {shown?.who ?? ""} linkje. Egyszer használható,{" "}
            <span className="data-mono">{minute(issued?.expires_at)}</span>-ig érvényes.
          </DialogDescription>
        </DialogHeader>
        {note !== null && <Callout tone={note.tone}>{note.text}</Callout>}
        <Field label="Link" htmlFor={inputId} hint="Most látod utoljára. Ha elveszik, adj ki újat — a régi akkor érvényét veszti.">
          <div className="flex gap-2">
            <Input id={inputId} readOnly value={url} className="font-mono" onFocus={(event) => event.target.select()} />
            <Button type="button" onClick={() => void copy()}>
              <CopyIcon />
              Másolás
            </Button>
          </div>
        </Field>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Kész
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- the invitation --------------------------------------------------------------------

function InviteDialog({
  open,
  onOpenChange,
  onInvited,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInvited: (row: UserRow, link: IssuedLink | null, mail: LinkMail | null) => void;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<Role>("marketer");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailId = useId();
  const nameId = useId();
  const roleId = useId();

  useEffect(() => {
    if (open) {
      setEmail("");
      setName("");
      setRole("marketer");
      setError(null);
      setBusy(false);
    }
  }, [open]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const answer = await inviteUser({ email: email.trim(), full_name: name.trim(), role });
      onInvited(answer.user, answer.link, answer.mail);
      onOpenChange(false);
    } catch (raised: unknown) {
      setError(messageOf(raised));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent>
        <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)}>
          <DialogHeader>
            <DialogTitle>Felhasználó meghívása</DialogTitle>
            <DialogDescription>
              A meghívott e-mailben egy egyszer használható linket kap, amellyel 72 órán belül beállítja a
              jelszavát. A linket itt is megmutatjuk, egyszer.
            </DialogDescription>
          </DialogHeader>
          <Field label="Email-cím" htmlFor={emailId}>
            <Input
              id={emailId}
              type="email"
              autoComplete="off"
              required
              maxLength={320}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoFocus
            />
          </Field>
          <Field label="Név" htmlFor={nameId}>
            <Input id={nameId} required maxLength={120} value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
          <Field
            label="Szerep"
            htmlFor={roleId}
            hint="Admin: felhasználókat és országbeállításokat kezel, feloldhat, leállíthat. Marketing: kampányok, sablonok, szegmensek."
          >
            <NativeSelect id={roleId} className="w-full" value={role} onChange={(event) => setRole(event.target.value as Role)}>
              {ROLES.map((value) => (
                <option key={value} value={value}>
                  {ROLE_LABEL[value]}
                </option>
              ))}
            </NativeSelect>
          </Field>
          {error !== null && <ErrorState message={error} />}
          <DialogFooter>
            <Button variant="outline" type="button" disabled={busy} onClick={() => onOpenChange(false)}>
              Mégse
            </Button>
            <Button type="submit" disabled={busy || email.trim() === "" || name.trim() === ""}>
              {busy ? "Folyamatban…" : "Meghívás"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// --- one user's log -----------------------------------------------------------------------

function History({ userId }: { userId: number }) {
  const [{ data, error }] = useLoad<UserEvent[]>(() => fetchUserEvents(userId), [userId]);
  if (error !== null) return <ErrorState message={error} />;
  if (data === null) return <p className="text-muted-foreground">Betöltés…</p>;
  if (data.length === 0) return <p className="text-muted-foreground">Nincs naplóbejegyzés.</p>;
  return (
    <ul className="m-0 flex list-none flex-col gap-1 rounded-control border border-line bg-surface p-3">
      {data.map((event, index) => (
        <li key={index} className="text-muted-foreground">
          <span className="data-mono">{minute(event.occurred_at)}</span> ·{" "}
          <strong className="font-medium text-foreground" title={event.action}>
            {eventLine(event)}
          </strong>{" "}
          · {actorLabel(event.actor)}
        </li>
      ))}
    </ul>
  );
}

// --- the page -------------------------------------------------------------------------------

/** What the confirmation dialog says for each action. */
function confirmCopy(action: UserAction, row: UserRow) {
  const who = row.full_name || row.email;
  switch (action.kind) {
    case "promote":
      return {
        title: `Admin jog: ${who}`,
        description: "Felhasználókat és országbeállításokat kezelhet, kizárást oldhat fel, leállíthatja a küldést. A következő kattintásától érvényes.",
        confirm: "Admin jog adása",
        tone: "default" as const,
      };
    case "demote":
      return {
        title: `Marketing szerep: ${who}`,
        description: "Az admin műveleteket a következő kattintásától nem érheti el.",
        confirm: "Marketing szerepre állítás",
        tone: "default" as const,
      };
    case "deactivate":
      return row.state === "active"
        ? {
            title: `Letiltás: ${who}`,
            description: "Azonnal kilép mindenhonnan, és nem tud belépni. Később visszakapcsolható.",
            confirm: "Letiltás",
            tone: "danger" as const,
          }
        : {
            title: `Meghívás visszavonása: ${who}`,
            description: "A kiadott link azonnal érvényét veszti.",
            confirm: "Meghívás visszavonása",
            tone: "danger" as const,
          };
    case "reactivate":
      return {
        title: `Visszakapcsolás: ${who}`,
        description:
          row.password_set_at !== null
            ? "Újra beléphet a régi jelszavával. A letiltás előtti belépései nem élednek fel."
            : "Még nem állított be jelszót: új meghívó linket kap.",
        confirm: "Visszakapcsolás",
        tone: "default" as const,
      };
    case "link":
      return {
        title: row.state === "active" ? `Jelszó-visszaállító link: ${who}` : `Új meghívó link: ${who}`,
        description:
          row.state === "active"
            ? "Egy új, egyszer használható link 24 órára. Amíg nem használja, a mostani jelszava is működik; ha használja, minden belépése megszűnik."
            : "Egy új, egyszer használható link 72 órára. A korábbi link érvényét veszti.",
        confirm: "Link kiadása",
        tone: "default" as const,
      };
  }
}

export default function UsersSettings() {
  const { user: me, logout } = useAuth();
  const [{ data, error }, reload] = useLoad<UserRow[]>(fetchUsers, []);
  const rows = useMemo(() => data ?? [], [data]);
  const [openId, setOpenId] = useState<number | null>(null);
  const [inviting, setInviting] = useState(false);
  const [pending, setPending] = useState<{ action: UserAction; row: UserRow } | null>(null);
  const [shown, setShown] = useState<Shown | null>(null);
  // The log under an open row is read again after every change.
  const [historyKey, setHistoryKey] = useState(0);

  async function run(action: UserAction, row: UserRow): Promise<void> {
    const who = row.full_name || row.email;
    switch (action.kind) {
      case "promote":
      case "demote":
        await changeUserRole(row.id, action.kind === "promote" ? "admin" : "marketer");
        toast.success(action.kind === "promote" ? "Admin jog megadva" : "Marketing szerepre állítva", {
          description: who,
        });
        break;
      case "deactivate":
        await deactivateUser(row.id);
        toast.success(row.state === "active" ? "Letiltva" : "Meghívás visszavonva", { description: who });
        if (me !== null && row.id === me.id) {
          logout();
          return;
        }
        break;
      case "reactivate": {
        const answer = await reactivateUser(row.id);
        toast.success("Visszakapcsolva", { description: who });
        if (answer.link !== null) setShown({ link: answer.link, who, email: row.email, mail: answer.mail });
        break;
      }
      case "link": {
        const answer = await issueUserLink(row.id);
        if (answer.link !== null) setShown({ link: answer.link, who, email: row.email, mail: answer.mail });
        break;
      }
    }
    setHistoryKey((key) => key + 1);
    reload();
  }

  const columns = useMemo<ColumnDef<UserRow>[]>(
    () => [
      {
        id: "name",
        header: "Név",
        cell: ({ row }) => (
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-medium">
              {row.original.full_name || "—"}
              {me !== null && row.original.id === me.id && <span className="text-muted-foreground"> (te)</span>}
            </span>
            <span className="truncate text-label text-muted-foreground">{row.original.email}</span>
          </div>
        ),
      },
      {
        id: "role",
        header: "Szerep",
        cell: ({ row }) => ROLE_LABEL[row.original.role] ?? row.original.role,
      },
      {
        id: "state",
        header: "Állapot",
        cell: ({ row }) => (
          <div className="flex flex-col items-start gap-0.5">
            <StatusPill domain="user" state={row.original.state} />
            {row.original.link !== null && (
              <span className="text-label text-muted-foreground">
                {LINK_WORD[row.original.link.purpose]} eddig: {minute(row.original.link.expires_at)}
              </span>
            )}
          </div>
        ),
      },
      {
        id: "last_login_at",
        header: "Utolsó belépés",
        meta: { mono: true, muted: true },
        cell: ({ row }) => minute(row.original.last_login_at),
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Műveletek</span>,
        meta: { align: "right" },
        cell: ({ row }) => (
          <RowActions
            label={`Műveletek: ${row.original.email}`}
            actions={userActions(row.original, rows).map((action) => ({
              label: action.blocked !== null ? `${action.label} — ${action.blocked}` : action.label,
              disabled: action.blocked !== null,
              danger: action.danger,
              onSelect: () => setPending({ action, row: row.original }),
            }))}
          />
        ),
      },
    ],
    [rows, me],
  );

  const copy = pending === null ? null : confirmCopy(pending.action, pending.row);

  return (
    <>
      <PageHeader
        title="Beállítások"
        description="Kik használhatják az admint, és milyen szereppel. Minden változás bekerül a felhasználó naplójába."
      />

      <div className="flex flex-col gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Országbeállítások</CardTitle>
            <CardDescription>
              Feladó, cégadatok és lábléc, követő domain, küldési ablak — országonként, a saját oldalukon.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline">
              <Link to="/kuldo-fiokok">
                <IdCardIcon />
                Országbeállítások megnyitása
              </Link>
            </Button>
          </CardContent>
        </Card>

        <DigestRecipients />

        <Webshops />

        <section className="flex flex-col gap-3" aria-labelledby="users-title">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="flex flex-col gap-1">
              <h2 id="users-title" className="m-0">
                Felhasználók
              </h2>
              <p className="m-0 text-muted-foreground">
                Admin: felhasználók, országbeállítások, feloldás, leállítás. Marketing: kampányok, sablonok, szegmensek.
              </p>
            </div>
            <Button type="button" onClick={() => setInviting(true)}>
              <UserPlusIcon />
              Meghívás
            </Button>
          </div>

          {error !== null && <ErrorState message={error} onRetry={reload} />}

          <DataTable
            label="Felhasználók"
            columns={columns}
            data={rows}
            loading={data === null && error === null}
            getRowId={(row) => String(row.id)}
            onRowClick={(row) => setOpenId((open) => (open === row.id ? null : row.id))}
            isRowSelected={(row) => row.id === openId}
            renderExpanded={(row) => (openId === row.id ? <History key={historyKey} userId={row.id} /> : null)}
            empty={<p className="px-4 py-3 text-muted-foreground">Még nincs felhasználó.</p>}
          />
        </section>
      </div>

      <InviteDialog
        open={inviting}
        onOpenChange={setInviting}
        onInvited={(row, link, mail) => {
          toast.success("Meghívva", { description: row.email });
          if (link !== null) setShown({ link, who: row.full_name || row.email, email: row.email, mail });
          reload();
        }}
      />

      <ActionDialog
        open={pending !== null}
        onOpenChange={(open) => !open && setPending(null)}
        title={copy?.title ?? ""}
        description={copy?.description}
        confirmLabel={copy?.confirm ?? ""}
        tone={copy?.tone ?? "default"}
        onConfirm={async () => {
          if (pending !== null) await run(pending.action, pending.row);
        }}
      />

      <LinkDialog shown={shown} onClose={() => setShown(null)} />
    </>
  );
}
